import React, { useState, useEffect, useContext } from "react";
import useGlobalInventory from "../../services/Inventory/useGlobalInventory";
import useInventory from "../../services/Inventory/useInventory";
import useRack from "../../services/Inventory/useRack";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "react-toastify";
import NoIndex from "../../components/NoIndex";

const NewPurchaseItemModel = ({ barcode, onClose,  onItemAdded }) => {
  const { fetchAllInventoryVariants, variants } = useGlobalInventory();
  const { getSubCategories, uploadItemImage, insertCatalogItem, getItemDetails } =
    useInventory();
  const { fetchRacks, rows: racks } = useRack();
  const { userID } = useContext(GlobalContext);

  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    itemName: "",
    barcode: "",
    status: "Y",
    vegIcon: "",
    description: "",
    tags: "",
    rating: "0",
    photo: "",
    rack: "",
    category: "",
    categoryInput: "",
    subcategory: "",
    subcategoryInput: "",
    variant: "",
    expiryDate: "",
    price: "",
    discount: "",
    discountedPrice: "",
    homeDeliveryPrice: "",
    takeawayPrice: "",
    diningPrice: "",
    roomOrderPrice: "",
    carOrderPrice: "",
  });

useEffect(() => {
  if (barcode) {
    setFormData((prev) => ({
      ...prev,
      barcode: barcode,
    }));
  }
}, [barcode]);

  useEffect(() => {
    fetchRacks();
    fetchAllInventoryVariants();
    loadSubCategories();
  }, []);

  const loadSubCategories = async () => {
    const data = await getSubCategories("");
    setSubCategories(data || []);
  };

  useEffect(() => {
    if (subCategories.length) {
      const unique = [
        ...new Set(
          subCategories
            .map((s) => s.category?.trim())
            .filter((c) => c && c !== ""),
        ),
      ];
      setCategories(unique);
    }
  }, [subCategories]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "price" || name === "discount") {
      const updated = { ...formData, [name]: value };
      const price = parseFloat(updated.price || 0);
      const discount = parseFloat(updated.discount || 0);

      updated.discountedPrice = price > 0 ? Math.max(price - discount, 0) : "";

      setFormData(updated);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onloadend = async () => {
      const res = await uploadItemImage({
        item_name: formData.itemName || `item_${Date.now()}`,
        image_data: reader.result,
      });

      if (res?.status === "0") {
        setFormData((prev) => ({
          ...prev,
          photo: res.image_url,
        }));
        toast.success("Image uploaded");
      }
    };

    reader.readAsDataURL(file);
  };

const handleSave = async () => {
  try {
    const selectedVariant = variants.find(
      (v) => String(v.variant_id) === String(formData.variant)
    );

    const selectedCategoryObj = subCategories.find(
      (s) =>
        s.category === (formData.categoryInput || formData.category)
    );

    const payload = {
      userid: userID,
      items: [
        {
          itemName: formData.itemName,
          itemId: "",
          status: formData.status,
          itemDescription: formData.description,
          dish: formData.tags,
          category: formData.categoryInput || formData.category,
          catId: selectedCategoryObj?.id || "", 
          price: String(formData.price || "0"),
          discount: String(formData.discount || "0"),
          discountPrice: String(formData.discountedPrice || "0"),
          home_delivery_price: String(formData.homeDeliveryPrice || "0"),
          takeaway_price: String(formData.takeawayPrice || "0"),
          dining_price: String(formData.diningPrice || "0"),
          room_order_price: String(formData.roomOrderPrice || "0"),
          car_order_price: String(formData.carOrderPrice || "0"),
          expiryDate: formData.expiryDate || "",
          rackNumber: formData.rack,
          variantId: formData.variant,
          variantName: selectedVariant?.variant_title || "",
          photo: formData.photo,
          iconDish: formData.vegIcon,
          barcodeNumber: formData.barcode,
          ratings: "0",
          customizations: "[]",
          categoryDescription: "",
          itemNameTrans: "",
          itemDescriptionTrans: "",
          position: "",
          sizeTrans: "",
          subCategory:
            formData.subcategoryInput || formData.subcategory || "",
        },
      ],
    };

const res = await insertCatalogItem(payload);

if (!res) {
  toast.error("Failed to add item");
  return;
}

const itemRes = await getItemDetails(formData.barcode);

if (itemRes && itemRes.responseCode === "00" && itemRes.response) {
  const item = itemRes.response; 
  onItemAdded({
    ...item,
    quantity: 1,
    purchasePrice: Number(item.price || 0),
  });
} else {
  onItemAdded({
    item_id: formData.barcode,
    item_name: formData.itemName,
    photo: formData.photo,
    size: "",
    quantity: 1,
    purchasePrice: Number(formData.price || 0),
  });
}

onClose();

  } catch (err) {
    console.error(err);
    toast.error("Error saving item");
  }
};

  return (
    <>
    <NoIndex/>

    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
      <div className="bg-white w-[1000px] rounded-xl shadow-lg montserrat-font-1">
        {/* HEADER */}
        <div className="flex justify-between items-start p-5 border-b montserrat-font-1">
          <div>
            <h2 className="text-xl font-semibold">Add New Item</h2>
            <p className="text-sm text-gray-500">
              Item not found. Create it and add it to purchase.
            </p>
          </div>
          <button onClick={onClose}>✕</button>
        </div>

        {/* BODY */}
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto montserrat-font-1">
          {/* ROW 1 */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="label">Item Name *</label>
              <input
                name="itemName"
                className="input"
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="label">Barcode Number</label>
              <input
                name="barcode"
                value={formData.barcode}
                className="input"
                readOnly
              />
            </div>

            <div>
              <label className="label">Status</label>
              <select name="status" className="input" onChange={handleChange}>
                <option value="Y">Active (Y)</option>
                <option value="N">Inactive (N)</option>
              </select>
            </div>

            <div>
              <label className="label">Veg / Non-Veg</label>
              <select name="vegIcon" className="input" onChange={handleChange}>
                <option value="">None</option>
                <option value="Y">Veg</option>
                <option value="N">Non-Veg</option>
              </select>
            </div>
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="label">Description</label>
            <textarea
              name="description"
              className="input"
              onChange={handleChange}
            />
          </div>

          {/* ROW 2 */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="label">Dish / Tags</label>
              <input name="tags" className="input" onChange={handleChange} />
            </div>

            <div>
              <label className="label">Ratings</label>
              <input value="0" className="input" />
            </div>

            <div>
              <label className="label">Photo</label>
              <input
                type="file"
                className="input"
                onChange={handleImageChange}
              />
            </div>

            <div>
              <label className="label">Rack Number</label>
              <select name="rack" className="input" onChange={handleChange}>
                <option value="">Select Rack</option>
                {racks?.map((r, i) => (
                  <option key={i}>{r.rackNumber}</option>
                ))}
              </select>
            </div>
          </div>

          {/* CATEGORY + SUBCATEGORY */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="label">Category (Select)</label>
              <select name="category" className="input" onChange={handleChange}>
                <option value="">Select Category</option>
                {categories.map((c, i) => (
                  <option key={i}>{c}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Category (Manual)</label>
              <input
                className="input"
                placeholder="Or type category"
                onChange={(e) =>
                  setFormData({ ...formData, categoryInput: e.target.value })
                }
              />
            </div>

            <div>
              <label className="label">Sub Category (Select)</label>
              <select
                name="subcategory"
                className="input"
                onChange={handleChange}
              >
                <option value="">Select Sub Category</option>

                {[
                  ...new Map(
                    subCategories
                      ?.filter(
                        (s) =>
                          s?.subCategory &&
                          s.subCategory.trim() !== "" &&
                          (!formData.category ||
                            s.category === formData.category),
                      )
                      .map((item) => [item.subCategory.trim(), item]),
                  ).values(),
                ].map((s, i) => (
                  <option key={i}>{s.subCategory}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Sub Category (Manual)</label>
              <input
                className="input"
                placeholder="Or type sub category"
                onChange={(e) =>
                  setFormData({ ...formData, subcategoryInput: e.target.value })
                }
              />
            </div>
          </div>

          {/* PRICE */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="label">Variant</label>
              <select name="variant" className="input" onChange={handleChange}>
                <option value="">Select Variant</option>
                {variants?.map((v, i) => (
                  <option key={i} value={v.variant_id}>
                    {v.variant_title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Expiry Date</label>
              <input
                type="date"
                name="expiryDate"
                className="input"
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="label">Price *</label>
              <input name="price" className="input" onChange={handleChange} />
            </div>

            <div>
              <label className="label">Discount</label>
              <input
                name="discount"
                className="input"
                onChange={handleChange}
              />
            </div>
          </div>

          {/* OTHER PRICES */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="label">Discount Price</label>
              <input
                value={formData.discountedPrice}
                className="input"
                readOnly
              />
            </div>

            <div>
              <label className="label">Home Delivery Price</label>
              <input
                name="homeDeliveryPrice"
                className="input"
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="label">Takeaway Price</label>
              <input
                name="takeawayPrice"
                className="input"
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="label">Dining Price</label>
              <input
                name="diningPrice"
                className="input"
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="label">Room Order Price</label>
              <input
                name="roomOrderPrice"
                className="input"
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="label">Car Order Price</label>
              <input
                name="carOrderPrice"
                className="input"
                onChange={handleChange}
              />
            </div>
          </div>

          {/* IMAGE PREVIEW (FOOTER KE UPAR) */}
          {formData.photo && (
            <div className="flex items-center gap-4 border-t pt-4">
              <div className="relative w-24 h-24">
                <img
                  src={formData.photo}
                  alt="preview"
                  className="w-full h-full object-cover rounded-lg border"
                />

                {/* REMOVE BUTTON */}
                <button
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, photo: "" }))
                  }
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                >
                  ✕
                </button>
              </div>

              <div className="text-sm text-gray-500">Image Preview</div>
            </div>
          )}

          {/* FOOTER */}
          <div className="flex justify-end gap-3 pt-4 border-t montserrat-font-1">
            <button onClick={onClose} className="px-4 py-2 border rounded-lg">
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 text-white rounded-lg"
            >
              Save Item
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
        }
        .label {
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 4px;
          display: block;
        }
      `}</style>
    </div>
    </>
  );
};

export default NewPurchaseItemModel;
